import React, { useState, useEffect, useMemo } from 'react';
import {
  Row, Col, Card, Spin, Space, Tag, Alert, Segmented, Tooltip as AntTooltip,
} from 'antd';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../../libs/api';
import {
  CONFIG_COLORS, INK, NEUTRAL, FAINT, GRID, money, moneyShort, pct, useNarrow,
} from './pipelineShared';

/**
 * What the systems we are selling right now are worth, and at what margin.
 *
 * Its own tab because it answers a different question from the pipeline next
 * to it and on a different population: that one is about jobs already sold and
 * waiting, this one is about what is being sold. They share no filter and no
 * date basis, and the only thing they had in common was the request that
 * fetched them - which is now split, so this tab no longer waits on six
 * thousand invoices it never reads.
 */

/** A tooltip that reads like a sentence rather than a key dump. */
function Panel({ title, rows, footer }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #E4E9F0', borderRadius: 6,
      padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
    >
      <div style={{ fontWeight: 600, color: INK, marginBottom: 6 }}>{title}</div>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'flex', gap: 16, justifyContent: 'space-between' }}>
          <span style={{ color: r.color || NEUTRAL }}>{r.label}</span>
          <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
        </div>
      ))}
      {footer && <div style={{ color: FAINT, marginTop: 6, maxWidth: 220 }}>{footer}</div>}
    </div>
  );
}

function PricingBlock({ data }) {
  const series = useMemo(() => data.series || [], [data.series]);
  const periods = useMemo(() => data.periods || [], [data.periods]);
  const narrow = useNarrow();
  /**
   * Percent or dollars, because they answer different questions.
   *
   * A margin percentage says how well a job was priced; a margin in dollars
   * says what it actually earned. They come apart whenever the system size
   * moves — a bigger battery at a thinner percentage can still be the better
   * sale — and a chart that only ever shows one of them hides that entirely.
   */
  const [gpMode, setGpMode] = useState('pct');
  const gpSuffix = gpMode === 'pct' ? '__gp' : '__gpDollar';
  const gpFormat = gpMode === 'pct' ? pct : money;
  const colorOf = useMemo(() => {
    const m = {};
    series.forEach((s, i) => { m[s.key] = CONFIG_COLORS[i % CONFIG_COLORS.length]; });
    return m;
  }, [series]);

  // One row per fortnight, every series beside it, so both charts share an x.
  const rows = useMemo(() => periods.map((p) => {
    const row = { label: p.label, from: p.from, to: p.to };
    for (const s of series) {
      const point = s.points.find((x) => x.bucket === p.bucket);
      row[`${s.key}__payment`] = point?.count ? point.paymentInclGst : null;
      row[`${s.key}__gst`] = point?.count ? point.gst : null;
      row[`${s.key}__gp`] = point?.count ? point.gpPct : null;
      row[`${s.key}__gpDollar`] = point?.count ? point.gp : null;
      row[`${s.key}__count`] = point?.count || 0;
      row[`${s.key}__merged`] = Boolean(point?.merged);
      row[`${s.key}__variants`] = point?.variants || [];
    }
    return row;
  }), [periods, series]);

  const noMargin = data.coverage?.marginSource === 'unavailable';

  /**
   * A dot that says something: filled when one configuration sold that
   * fortnight, ringed when several were merged into the point.
   *
   * The merge is the one thing a price line cannot show on its own — a step in
   * the line is either the market moving or the product changing underneath
   * it, and those are opposite conclusions. Marking the node lets the reader
   * tell which they are looking at.
   */
  const MergeDot = ({ cx, cy, payload, dataKey, stroke }) => {
    if (cx == null || cy == null) return null;
    const key = dataKey.replace('__payment', '').replace('__gpDollar', '').replace('__gp', '');
    const merged = payload[`${key}__merged`];
    return merged
      ? <circle cx={cx} cy={cy} r={5} fill="#fff" stroke={stroke} strokeWidth={2} />
      : <circle cx={cx} cy={cy} r={3} fill={stroke} />;
  };

  const trendTooltip = (suffix, fmt) => ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    const shown = payload.filter((p) => p.value != null);
    if (!shown.length) return null;
    const merged = shown.filter((p) => row[`${p.dataKey.replace(suffix, '')}__merged`]);
    return (
      <Panel
        title={`${dayjs(row.from).format('D MMM')} – ${dayjs(row.to).format('D MMM YYYY')}`}
        rows={shown.map((p) => {
          const key = p.dataKey.replace(suffix, '');
          const s = series.find((x) => x.key === key);
          return {
            label: `${s?.label} (${row[`${key}__count`]})`,
            value: suffix === '__payment' && row[`${key}__gst`] != null
              ? `${fmt(p.value)}  (incl ${money(row[`${key}__gst`])} GST)`
              : fmt(p.value),
            color: p.color,
          };
        })}
        footer={merged.length
          ? `Merged: ${merged.map((p) => (row[`${p.dataKey.replace(suffix, '')}__variants`] || [])
            .map((v) => `${v.label}×${v.count}`).join(' + ')).join('; ')}`
          : null}
      />
    );
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {series.map((s, i) => (
          <Col xs={24} sm={12} xl={6} key={s.key}>
            <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
              <Space size={6} style={{ marginBottom: 2 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 2,
                  background: CONFIG_COLORS[i % CONFIG_COLORS.length], display: 'inline-block',
                }}
                />
                <span style={{ fontSize: 12, color: INK, fontWeight: 600 }}>{s.label}</span>
              </Space>
              <div style={{ fontSize: 22, color: INK, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                {money(s.avgPaymentInclGst)}
              </div>
              <div style={{ fontSize: 11, color: FAINT }}>
                average customer payment, incl GST · {s.count} sold
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: NEUTRAL, lineHeight: 1.7 }}>
                <div>
                  of which {money(s.avgGst)} GST &nbsp;·&nbsp; {money(s.avgPayment)} ex-GST
                </div>
                <div>+ {money(s.avgSubsidy)} subsidy, paid by the schemes</div>
                <div>
                  GP&nbsp;
                  <b style={{ color: s.avgGpPct == null ? FAINT : INK }}>{pct(s.avgGpPct)}</b>
                  {s.avgGp != null && (
                    <span style={{ color: FAINT }}>&nbsp;·&nbsp;{money(s.avgGp)}</span>
                  )}
                  {s.merged && (
                    <AntTooltip title={s.variants.map((v) => `${v.label} × ${v.count} (${v.recent} lately)`).join('  ·  ')}>
                      <Tag style={{ marginLeft: 8, fontSize: 10, lineHeight: '16px' }} color="default">
                        {s.variants.length} configs merged
                      </Tag>
                    </AntTooltip>
                  )}
                </div>
                {/*
                  * When it last sold, not only how often.
                  *
                  * These series are picked for being current, so a line that
                  * stops short is the chart saying the configuration went
                  * quiet — which is worth reading, and invisible if the card
                  * only ever reports a six-month average.
                  */}
                <div style={{ color: FAINT }}>
                  {s.recentCount} sold lately · last {dayjs(s.lastSoldAt).format('D MMM')}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {noMargin && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Gross profit is unavailable"
          description="The quote database that holds the designed margin did not answer. Prices on this block come from the CRM and are unaffected; only the GP% line is missing."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card
            size="small"
            bodyStyle={{ padding: '12px 16px' }}
            title={<span style={{ fontSize: 13 }}>Customer payment, incl GST</span>}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={rows} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: NEUTRAL }} interval={narrow ? 2 : 0} />
                <YAxis
                  tick={{ fontSize: 11, fill: NEUTRAL }}
                  tickFormatter={moneyShort}
                  width={56}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={trendTooltip('__payment', money)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={`${s.key}__payment`}
                    name={s.label}
                    stroke={colorOf[s.key]}
                    strokeWidth={2}
                    dot={<MergeDot />}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            size="small"
            bodyStyle={{ padding: '12px 16px' }}
            title={<span style={{ fontSize: 13 }}>Gross profit</span>}
            extra={(
              <Segmented
                size="small"
                value={gpMode}
                onChange={setGpMode}
                options={[{ label: '%', value: 'pct' }, { label: '$', value: 'dollar' }]}
              />
            )}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={rows} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: NEUTRAL }} interval={narrow ? 2 : 0} />
                <YAxis
                  tick={{ fontSize: 11, fill: NEUTRAL }}
                  tickFormatter={gpMode === 'pct' ? (v) => `${v}%` : moneyShort}
                  width={gpMode === 'pct' ? 48 : 56}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={trendTooltip(gpSuffix, gpFormat)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={`${s.key}${gpSuffix}`}
                    name={s.label}
                    stroke={colorOf[s.key]}
                    strokeWidth={2}
                    dot={<MergeDot />}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <div style={{ fontSize: 11, color: FAINT, marginTop: 8, lineHeight: 1.7 }}>
        Upfront sales only, by closing date, in fortnightly buckets over the last{' '}
        {data.months} months — {data.coverage?.upfrontSales} sales, of which{' '}
        {data.coverage?.withMargin} carry a quoted margin.
        {data.selection && (
          <>
            {' '}Which four configurations are plotted is decided on the most recent
            sales, not the six-month average — hybrids from{' '}
            {dayjs(data.selection.Hybrid?.from).format('D MMM')} and retrofits from{' '}
            {dayjs(data.selection['Battery Retrofit']?.from).format('D MMM')}, widened
            a fortnight at a time until there were enough sales to judge from. Ranking
            over the whole window instead picks whatever was most popular on average,
            which keeps a superseded configuration on the chart with its line stopping
            short while the thing that replaced it never appears.
          </>
        )}
        Price is what the customer actually pays, GST included, which is the figure
        on their invoice. How much of it is tax is written out on each card, since
        that part is remitted rather than earned. The subsidy stays separate: it is
        revenue to us but set by policy, and a scheme step change blended into the
        price would read as a price cut. Margin is struck ex-GST against payment
        plus subsidy — the tax was never revenue, and a margin measured against it
        would be a tenth too flattering.
        A hollow node marks a fortnight where more than one configuration sold
        under the same series — hover it to see which.
      </div>
    </>
  );
}


export default function Trending() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get('/api/retail-finance/pipeline', { params: { block: 'pricing' }, timeout: 120000 })
      .then((r) => { if (live) { setData(r.data?.data?.pricing || null); setError(null); } })
      .catch((e) => { if (live) setError(e.response?.data?.message || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  if (error) {
    return <Alert type="error" showIcon message="Could not load the trend" description={error} />;
  }

  return (
    <Spin spinning={loading} tip="Reading the quotes behind the last six months of sales…">
      <div style={{ minHeight: 200 }}>
        {data && <PricingBlock data={data} />}
      </div>
    </Spin>
  );
}
